/**
 * Hook to lazy load theme colour enrichment for a single theme
 * For multiple themes, use useThemesColorEnrichment instead
 */

'use client';

import { useEffect, useState } from 'react';

/**
 * Enriched theme data returned by the hook
 */
export interface EnrichedThemeData {
  /** Theme colour (if available) */
  color: string | null;
  /** Theme name (if available) */
  name: string | null;
  /** Whether data is currently being fetched */
  isLoading: boolean;
}

/**
 * Hook to lazy load theme colour and name enrichment
 * Only fetches if theme doesn&apos;t already have a colour or name
 *
 * @param themeId - Theme ID to fetch data for
 * @param hasColor - Whether theme already has a colour
 * @param hasName - Whether theme already has a name
 * @returns Enriched theme data with colour and name
 */
export function useThemeColorEnrichment(
  themeId: string,
  hasColor: boolean,
  hasName: boolean = false
): EnrichedThemeData {
  const [enrichedData, setEnrichedData] = useState<EnrichedThemeData>({
    color: null,
    name: null,
    isLoading: false,
  });

  useEffect(() => {
    // Don't fetch if theme already has both colour and name, or no themeId
    if ((hasColor && hasName) || !themeId) {
      return;
    }

    let cancelled = false;

    async function fetchThemeData() {
      setEnrichedData(prev => ({ ...prev, isLoading: true }));

      try {
        // Dynamic import to avoid bundling server-side code
        const { getThemesByIds } = await import('../api/client');

        const themes = await getThemesByIds([themeId]);

        if (!cancelled && themes.length > 0) {
          const theme = themes.find(t => t.id === themeId) || themes[0];
          setEnrichedData({
            color: theme.color || null,
            name: theme.name || null,
            isLoading: false,
          });
        } else if (!cancelled) {
          setEnrichedData(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.warn('[useThemeColorEnrichment] Failed to fetch theme data:', error);
        if (!cancelled) {
          setEnrichedData(prev => ({ ...prev, isLoading: false }));
        }
      }
    }

    fetchThemeData();

    return () => {
      cancelled = true;
    };
  }, [themeId, hasColor, hasName]);

  return enrichedData;
}
